import { useId } from 'react'
import type { CSSProperties } from 'react'
import './ww-loader.css'

/**
 * The WonderWorks loader: the W dribbles its two dots
 * -------------------------------------------------------------
 * Huanrui's design, from `wonderworks-loader/` (2026-09-28), which also holds
 * the stand-alone SVGs and a preview page. Shown wherever something is being
 * MADE -- a picture now, a video later -- in place of the sweep that used to
 * cross an empty frame.
 *
 * In colour and at full strength, like the logo itself -- tried at sixty per
 * cent and put back at Huanrui's word. The gradient appears only inside the
 * mark, which is the rule the logo already follows.
 *
 * Pure CSS on transform and opacity; nothing to fetch, no timer, and nothing
 * that touches the request it is waiting on.
 */

type Props = {
  /** CSS width; height follows the 500:264 shape. Default 112. */
  width?: number | string
  /** `brand` is the logo gradient; `mono` follows `color`. */
  variant?: 'brand' | 'mono'
  /** Seconds per dribble. Default 1.2. */
  speed?: number
  /** Read out once by screen readers. */
  label?: string
  className?: string
}

const STOPS: [string, string][] = [
  ['0.0', '#74C7EC'],
  ['0.06', '#4AA3C3'],
  ['0.11', '#2B75A4'],
  ['0.17', '#1C5791'],
  ['0.23', '#1D4889'],
  ['0.29', '#4A3D80'],
  ['0.36', '#783372'],
  ['0.44', '#9D2066'],
  ['0.52', '#C6386C'],
  ['0.58', '#EE5670'],
  ['0.65', '#F3735B'],
  ['0.72', '#F58640'],
  ['0.8', '#F69628'],
  ['0.9', '#FCAE2E'],
  ['1.0', '#FEC72F'],
]

const BODY = 'M100 92V172A50 50 0 0 0 200 172V92A50 50 0 0 1 300 92V172A50 50 0 0 0 400 172V92'
const HAND_L = 'M50 42A50 50 0 0 1 100 92'
const HAND_R = 'M400 92A50 50 0 0 1 450 42'

export function WwLoader({
  width = 112,
  variant = 'brand',
  speed = 1.2,
  label = 'Generating',
  className,
}: Props) {
  // One id per instance. Several loaders on one page sharing an id break as
  // soon as the first of them is hidden: the others lose their gradient.
  const gid = `ww-g-${useId().replace(/:/g, '')}`
  const mono = variant === 'mono'
  const style = {
    width,
    height: 'auto',
    '--ww-t': `${speed}s`,
    '--ww-paint': mono ? 'currentColor' : `url(#${gid})`,
  } as CSSProperties

  return (
    <svg
      viewBox="0 0 500 264"
      className={`ww-ld${mono ? ' ww-ld--mono' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      role="status"
      aria-label={label}
    >
      {mono ? null : (
        <defs>
          <linearGradient id={gid} gradientUnits="userSpaceOnUse" x1="16" y1="0" x2="484" y2="0">
            {STOPS.map(([offset, colour]) => (
              <stop key={offset} offset={offset} stopColor={colour} />
            ))}
          </linearGradient>
        </defs>
      )}
      <ellipse className="ww-sh" cx="34" cy="243" rx="18" ry="3.5" />
      <ellipse className="ww-sh ww-r" cx="466" cy="243" rx="18" ry="3.5" />
      <circle className="ww-b" cx="34" cy="132" r="18" />
      <circle className="ww-b ww-r" cx="466" cy="132" r="18" />
      <path className="ww-s" d={BODY} />
      <path className="ww-s ww-hl" d={HAND_L} />
      <path className="ww-s ww-hr ww-r" d={HAND_R} />
    </svg>
  )
}

/**
 * The loader centred in an empty frame, sized to the frame, with a band of
 * light sweeping across IN FRONT of it.
 *
 * The sweep is what says "loading": the loader on its own read as the logo
 * idling rather than as something on its way (Huanrui, 2026-09-28), and the
 * sweep is the cue everybody already knows from empty placeholders. In front,
 * so it passes over the mark as well as the frame, as asked.
 *
 * GREY here, not in colour (Huanrui, 2026-09-28): inside a placeholder the
 * picture that is coming is the point, and a coloured mark competed with the
 * pictures already on the page. The Miro board keeps the coloured GIFs.
 *
 * For the placeholders that hold a picture's place while it is made: the
 * chat's, the generate page's, the canvas's. The frame keeps its own size and
 * background; this only fills it. About two fifths of the frame's width,
 * never smaller than it stays readable (about 56px) nor larger than it was
 * designed for.
 */
export function WwLoaderFill({
  label,
  size = 'normal',
}: {
  label?: string
  /**
   * `large` is twice the size, for the bigger frames -- the generate page
   * and the canvas, where the normal one looked lost (Huanrui, 2026-09-28).
   */
  size?: 'normal' | 'large'
}) {
  return (
    <span className={`ww-ld-fill${size === 'large' ? ' ww-ld-fill--large' : ''}`}>
      <WwLoader width={size === 'large' ? '80%' : '40%'} variant="mono" label={label} />
      <span className="ww-ld-sweep" aria-hidden="true" />
    </span>
  )
}

export default WwLoader
